import React, { useEffect, useState } from 'react';
import { providerBookingApi } from '../api/providerBookingApi';
import { openInvoicePrint } from '../utils/invoicePrint';
import { formatHours } from '../utils/duration';

// Amounts arrive from the backend already in dollars (formatInvoice → /100).
const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmt = (d) =>
  d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const STATUS = {
  awaiting_release: { label: 'Awaiting release', className: 'bg-amber-50 text-amber-700' },
  released: { label: 'Released', className: 'bg-emerald-50 text-emerald-700' },
};

const ITEM_STATUS = {
  booked: { label: 'Booked', className: 'bg-emerald-50 text-emerald-700' },
  included: { label: 'Included', className: 'bg-emerald-50 text-emerald-700' },
  pending: { label: 'Pending decision', className: 'bg-amber-50 text-amber-700' },
  accepted: { label: 'Accepted — payment due', className: 'bg-amber-50 text-amber-700' },
  paid: { label: 'Accepted & paid', className: 'bg-emerald-50 text-emerald-700' },
  rejected: { label: 'Rejected', className: 'bg-red-50 text-red-600' },
  adjustment: { label: 'Adjustment', className: 'bg-slate-100 text-slate-600' },
};

// Read-only Gigger invoice for one completed booking. The provider endpoint verifies
// that the signed-in Gigger owns the booking and can only return gigger_payout data.
const PayoutStatement = ({ bookingId }) => {
  const [inv, setInv] = useState(null);
  const [state, setState] = useState('loading'); // loading | ready | not_ready | error
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await providerBookingApi.payoutInvoice(bookingId);
        if (cancelled) return;
        setInv(res.data || null);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setState('not_ready');
        } else {
          setError(err.response?.data?.message || "Couldn't load your payout invoice.");
          setState('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  if (state === 'loading') return null;

  if (state === 'not_ready') {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
        <div className="mt-2 text-[13px] leading-5 text-giggs-muted">
          Your payout invoice will appear here once the client has paid  .
        </div>
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
        <div className="mt-2 text-[13px] text-red-600">{error}</div>
      </section>
    );
  }

  if (!inv) return null;
  const status = STATUS[inv.status] || STATUS.awaiting_release;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <img src="/images/logo.webp" alt="GIGGS" className="h-auto w-[96px]" />
          <div className="mt-3 font-nunito text-sm font-bold text-giggs-ink">Gigger payout invoice</div>
          <div className="mt-1 text-xs text-giggs-muted">{inv.invoiceNumber} · issued {fmt(inv.issuedAt)}</div>
        </div>
        <span className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-bold ${status.className}`}>{status.label}</span>
      </div>

      <div className="py-4">
        {(inv.lineItems || []).map((item, index) => {
          const itemStatus = ITEM_STATUS[item.status] || ITEM_STATUS.booked;
          const title = item.taskName || item.description || item.serviceName || 'Line item';
          const isIncluded = item.category === 'included_task';
          const amount = isIncluded ? 'Included' : (item.status === 'pending' ? 'Pending' : money(item.amount));
          return (
            <div
              key={`${item.description}-${index}`}
              className={`flex items-start justify-between gap-4 border-b border-slate-50 py-3 text-sm last:border-0 ${item.status === 'rejected' ? 'opacity-70' : ''}`}
            >
              <div className="min-w-0">
                <div className="break-words font-semibold text-giggs-ink">{title}</div>
                {item.serviceName && item.serviceName !== title && (
                  <div className="mt-0.5 text-xs font-semibold text-giggs-green">{item.serviceName}</div>
                )}
                {item.details && <div className="mt-1 whitespace-pre-wrap text-xs leading-5 text-giggs-muted">{item.details}</div>}
                <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-giggs-muted">
                  <span className={`rounded-full px-2 py-0.5 font-bold ${itemStatus.className}`}>{itemStatus.label}</span>
                  {item.hours != null && <span>{formatHours(item.hours)}</span>}
                  {item.unitAmount != null && <span>{money(item.unitAmount)}/hr</span>}
                </div>
              </div>
              <span className={`shrink-0 font-semibold ${item.status === 'rejected' ? 'text-red-600' : 'text-giggs-ink'}`}>{amount}</span>
            </div>
          );
        })}
        <div className="mt-3 flex items-center justify-between gap-4 border-t border-slate-100 pt-3 text-[15px] font-extrabold text-giggs-ink">
          <span>Your earnings</span>
          <span className="text-giggs-green">{money(inv.total)}</span>
        </div>
      </div>

      <div className="text-xs leading-5 text-giggs-muted">
        {inv.status === 'released'
          ? `Released${inv.releasedAt ? ` on ${fmt(inv.releasedAt)}` : ''} — the transfer is on its way to your connected bank account.`
          : 'GIGGS will release your payout to your connected account after review. No action is needed from you.'}
      </div>

      <button
        type="button"
        onClick={() => openInvoicePrint({ invoice: inv, kind: 'gigger' })}
        className="mt-4 appearance-none cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-giggs-green transition hover:border-giggs-green"
      >
        View / Print invoice
      </button>
    </section>
  );
};

export default PayoutStatement;